import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import {
  Navigate,
  createBrowserRouter,
  createMemoryRouter,
  type RouteObject,
} from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageSpinner } from '@/components/ui';
import { ComingSoon } from '@/features/placeholder/ComingSoon';
import { NotFoundPage, RouteErrorPage } from '@/features/errors/ErrorPages';
import { ChangePasswordPageLazy, LoginPageLazy } from './authPages';
import { RequireAuth, RequireRole } from './guards';
import { RootLayout } from './RootLayout';
import { HOME_PATH, extraRoutes, navEntries, type Audience, type NavEntry } from './registry';

/** Every `src/features/<screen>/index.tsx` that exists, discovered at build time. */
const screenModules = import.meta.glob<{ default: ComponentType }>('../features/*/index.tsx');

type ScreenRef = Pick<NavEntry, 'screen'> & Partial<NavEntry>;

/** Built once at module load (never during render): the real screen if its folder exists, else the placeholder. */
function screenElement(entry: ScreenRef): ReactElement {
  const loader = screenModules[`../features/${entry.screen}/index.tsx`];
  if (!loader) return <ComingSoon entry={entry as NavEntry} />;
  const Page = lazy(loader);
  return (
    <Suspense fallback={<PageSpinner />}>
      <Page />
    </Suspense>
  );
}

function guarded(audience: Audience, feature: NavEntry['feature'], entry: ScreenRef) {
  return (
    <RequireRole audience={audience} feature={feature}>
      {screenElement(entry)}
    </RequireRole>
  );
}

export const routeObjects: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <LoginPageLazy /> },
      {
        // forced/voluntary password change: authenticated, but outside the shell
        element: <RequireAuth />,
        children: [{ path: '/change-password', element: <ChangePasswordPageLazy /> }],
      },
      {
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to={HOME_PATH} replace /> },
          ...navEntries.map((e) => ({
            path: `${e.path}/*`,
            element: guarded(e.audience, e.feature, e),
          })),
          ...extraRoutes.map((r) => ({
            path: `${r.path}/*`,
            element: guarded(r.audience, undefined, { screen: r.screen }),
          })),
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(routeObjects);
export const createTestRouter = (initialEntries: string[]) =>
  createMemoryRouter(routeObjects, { initialEntries });
